import React, { useState, useEffect, useRef } from "react";
import Input from "../../components/inputField";
import { StyledRow } from "./styles";
import { getCountryName } from "common/countryName";

export const AddressAutoFields = ({
  data = {},
  setData,
  isMobile,
  hasMargin,
}) => {
  const autocompleteRef = useRef(null);

  const [addressFields, setAddressFields] = useState({
    address: data.address || data.company?.address || "",
    suite: data.suite || data.company?.suite || "",
    postal: data.postal || data.company?.postal || "",
    city: data.city || data.company?.city || "",
    province: data.province || data.company?.province || "",
    country: data.country || data.company?.country || "",
  });

  const postalCode = addressFields.postal;
  const canadianPostalCodePattern = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
  const usaZipCodePattern = /^\d{5}(?:[-\s]\d{4})?$/;

  useEffect(() => {
    setAddressFields({
      address: data.address || data.company?.address || "",
      suite: data.suite || data.company?.suite || "",
      postal: data.postal || data.company?.postal || "",
      city: data.city || data.company?.city || "",
      province: data.province || data.company?.province || "",
      country: data.country || data.company?.country || "",
    });
  }, [data]);

  function initAutocomplete() {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.error("Google Maps API not loaded");
      return;
    }

    const address1Field = document.querySelector("#ship-address");
    if (!address1Field) {
      console.error("Address field #ship-address not found");
      return;
    }

    try {
      const country = addressFields.country || "CA";

      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(
          autocompleteRef.current,
        );
      }

      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        address1Field,
        {
          componentRestrictions: { country: [country] },
          fields: ["address_components", "geometry"],
          types: ["address"],
        },
      );

      autocompleteRef.current.addListener("place_changed", fillInAddress);
      console.log("Autocomplete initialized successfully on", address1Field);
    } catch (error) {
      console.error("Error initializing autocomplete:", error);
    }
  }

  function fillInAddress() {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    if (!place || !place.address_components) {
      console.error("No place or address components available");
      return;
    }

    console.log("Place selected:", place);

    let address1 = "";
    let postcode = "";
    let city = "";
    let province = "";
    let country = "";

    for (const component of place.address_components) {
      const componentType = component.types[0];

      switch (componentType) {
        case "street_number": {
          address1 = `${component.long_name} ${address1}`;
          break;
        }
        case "route": {
          address1 += component.short_name;
          break;
        }
        case "postal_code": {
          postcode = `${component.long_name}${postcode}`;
          break;
        }
        case "postal_code_suffix": {
          postcode = `${postcode}-${component.long_name}`;
          break;
        }
        case "locality":
          city = component.long_name;
          break;
        case "administrative_area_level_1": {
          province = component.short_name;
          break;
        }
        case "country": {
          country = component.short_name;
          break;
        }
      }
    }

    const address1Field = document.querySelector("#ship-address");
    const address2Field = document.querySelector("#address2");
    const postalField = document.querySelector("#postcode");
    const cityField = document.querySelector("#locality");
    const provinceField = document.querySelector("#state");
    const countryField = document.querySelector("#country");

    if (address1Field) address1Field.value = address1;
    if (postalField) postalField.value = postcode;
    if (cityField) cityField.value = city;
    if (provinceField) provinceField.value = province;
    if (countryField) countryField.value = country;

    const newAddressFields = {
      ...addressFields,
      address: address1,
      postal: postcode,
      city: city,
      province: province,
      country: country,
    };

    setAddressFields(newAddressFields);

    if (setData) {
      if (typeof setData === "function") {
        if (setData.length === 1) {
          setData({ company: newAddressFields });
        } else {
          Object.entries(newAddressFields).forEach(([key, value]) => {
            setData("company." + key, value);
          });
        }
      }
    }

    if (address2Field) {
      address2Field.focus();
    }

    console.log("Address fields updated:", newAddressFields);
  }

  const loadScript = (url, callback) => {
    // Check if script already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      callback();
      return;
    }

    let script = document.createElement("script");
    script.type = "text/javascript";

    if (script.readyState) {
      script.onreadystatechange = function () {
        if (
          script.readyState === "loaded" ||
          script.readyState === "complete"
        ) {
          script.onreadystatechange = null;
          callback();
        }
      };
    } else {
      script.onload = () => callback();
    }

    script.src = url;
    document.getElementsByTagName("head")[0].appendChild(script);
  };

  const handleFieldUpdate = (field, value) => {
    setAddressFields((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (setData && typeof setData === "function") {
      if (setData.length === 2) {
        setData("company." + field, value);
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=AIzaSyCwLidsAgErglXnD9dT6qnaQF7u_DYU3Pc&libraries=places`,
        () => {
          console.log("Google Maps script loaded");
          setTimeout(initAutocomplete, 300); // Add a slight delay to ensure DOM is ready
        },
      );
    }, 300);

    return () => {
      clearTimeout(timer);
      if (autocompleteRef.current) {
        if (window.google && window.google.maps && window.google.maps.event) {
          window.google.maps.event.clearInstanceListeners(
            autocompleteRef.current,
          );
        }
      }
    };
  }, []);

  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      const timer = setTimeout(initAutocomplete, 300);
      return () => clearTimeout(timer);
    }
  }, [addressFields.country]);

  return (
    <>
      <StyledRow isMobile={isMobile}>
        <Input
          id="twoThirdInputWrap"
          inputId="ship-address"
          required
          autocomplete="off"
          label="Address"
          type="text"
          placeholder="Start typing an address..."
          labelSize="12px"
          fontSize="18px"
          paddingRight="0px"
          style={{ marginRight: hasMargin ? "16px" : "0" }}
          value={addressFields.address}
          setValue={(val) => handleFieldUpdate("address", val)}
          validateFunc={(item) => item.length > 0}
          noCheckIcon={true}
          noErrorBorder={true}
        />
        <Input
          id="oneThirdInputWrap"
          inputId="address2"
          required
          autocomplete="off"
          label="Suite"
          type="text"
          placeholder=""
          labelSize="12px"
          fontSize="18px"
          style={{ marginRight: hasMargin ? "16px" : "0" }}
          value={addressFields.suite}
          setValue={(val) => handleFieldUpdate("suite", val)}
          validateFunc={(item) => item.length > 0}
          noCheckIcon={true}
          noErrorBorder={true}
        />

        <Input
          id="twoThirdInputWrap"
          inputId="postcode"
          required
          autocomplete="off"
          label={
            canadianPostalCodePattern.test(postalCode)
              ? "Postal Code"
              : usaZipCodePattern.test(postalCode)
                ? "Zip Code"
                : "Postal/Zip Code"
          }
          type="text"
          placeholder=""
          labelSize="12px"
          fontSize="18px"
          value={addressFields.postal}
          setValue={(val) => handleFieldUpdate("postal", val)}
          validateFunc={(item) => item.length > 0}
          noCheckIcon={true}
          noErrorBorder={true}
        />
      </StyledRow>
      <StyledRow isMobile={isMobile}>
        <Input
          id="twoThirdInputWrap"
          inputId="locality"
          required
          autocomplete="off"
          label="City"
          type="text"
          placeholder=""
          labelSize="12px"
          fontSize="18px"
          style={{ marginRight: hasMargin ? "16px" : "0" }}
          value={addressFields.city}
          setValue={(val) => handleFieldUpdate("city", val)}
          validateFunc={(item) => item.length > 0}
          noCheckIcon={true}
          noErrorBorder={true}
        />
        <Input
          id="oneThirdInputWrap"
          inputId="state"
          required
          autocomplete="off"
          label={
            canadianPostalCodePattern.test(postalCode)
              ? "Province"
              : usaZipCodePattern.test(postalCode)
                ? "State"
                : "State/Province"
          }
          type="text"
          placeholder=""
          labelSize="12px"
          fontSize="18px"
          style={{ marginRight: hasMargin ? "16px" : "0" }}
          value={addressFields.province}
          setValue={(val) => handleFieldUpdate("province", val)}
          validateFunc={(item) => item.length > 0}
          noCheckIcon={true}
          noErrorBorder={true}
        />

        <Input
          id="twoThirdInputWrap"
          inputId="country"
          required
          autocomplete="off"
          label="Country"
          type="text"
          placeholder=""
          labelSize="12px"
          fontSize="18px"
          value={addressFields.country}
          setValue={(val) => handleFieldUpdate("country", val)}
          validateFunc={(item) => item.length > 0}
          noCheckIcon={true}
          noErrorBorder={true}
        />
      </StyledRow>
    </>
  );
};
